// The WarmHub shape-definition format, normalized: what the HubRouter checks
// answers against, and what the code generator stores.

import { Effect, Option, Result, Schema, SchemaGetter } from "effect";
import { constraintTest, primitiveOf, withoutQuestion } from "./field-format.ts";
import { isJsonObject, Json, type JsonObject } from "./json.ts";

// FieldDef's fields but the two that make it recursive, `items` and `fields`.
// Its bounds are finite: JSON, and so WarmHub, has no NaN or Infinity, and the
// Curator API's OpenAPI document gives clients plain numbers.
const fieldDefFields = {
  type: Schema.String,
  optional: Schema.optionalKey(Schema.Boolean),
  shape: Schema.optionalKey(Schema.String),
  binding: Schema.optionalKey(Schema.String),
  enum: Schema.optionalKey(Schema.mutable(Schema.Array(Schema.String))),
  description: Schema.optionalKey(Schema.String),
  minLength: Schema.optionalKey(Schema.Finite),
  maxLength: Schema.optionalKey(Schema.Finite),
  pattern: Schema.optionalKey(Schema.String),
  minimum: Schema.optionalKey(Schema.Finite),
  maximum: Schema.optionalKey(Schema.Finite),
  integer: Schema.optionalKey(Schema.Boolean),
  minItems: Schema.optionalKey(Schema.Finite),
  maxItems: Schema.optionalKey(Schema.Finite),
};

/**
 * One field of a shape definition, normalized. `type` is WarmHub's field
 * type, such as "string", "number", "boolean", "wref" or "array", or "object"
 * for a nested object, whose fields are `fields`; a type the answer check
 * doesn't know accepts any value. Only `items` and `fields` are written out
 * here, because they make the type recursive; the rest comes from the
 * schema's fields.
 */
export interface FieldDef extends Schema.Struct.Type<typeof fieldDefFields> {
  readonly items?: FieldDef;
  readonly fields?: { readonly [name: string]: FieldDef };
}

/** Decodes a FieldDef read back from where the platform stored it, as ShapeDef does. */
export const FieldDef = Schema.Struct({
  ...fieldDefFields,
  items: Schema.optionalKey(Schema.suspend((): Schema.Codec<FieldDef> => FieldDef)),
  fields: Schema.optionalKey(Schema.Record(Schema.String, Schema.suspend((): Schema.Codec<FieldDef> => FieldDef))),
});

/**
 * Decodes a ShapeDef read back from where the platform stored it, such as the
 * shape definition cache in KV.
 */
export const ShapeDef = Schema.Struct({
  /** The shape's repository, such as "warmhub-data/us.geography". */
  repo: Schema.String,
  /** The shape's name, such as "Place". */
  name: Schema.String,
  version: Schema.Number,
  description: Schema.optionalKey(Schema.String),
  composes: Schema.optionalKey(Schema.mutable(Schema.Array(Schema.String))),
  fields: Schema.Record(Schema.String, FieldDef),
});
/**
 * One version of a WarmHub shape's definition, normalized: what the HubRouter
 * checks an assertion's data against (Design > Answer shapes, rule 5). Build
 * one from WarmHub's data with shapeDefFromWarmHub.
 */
export type ShapeDef = typeof ShapeDef.Type;

/**
 * A definition's pinned shape ref, "wh:org/repo/Shape/Name@v2": the key
 * validateAnswer's `defs` and an assertion's `shape` use.
 */
export function shapeRef(def: ShapeDef): string {
  return `wh:${def.repo}/Shape/${def.name}@v${def.version}`;
}

// WarmHub's shape data, read leniently: shapeDefFromWarmHub never fails. A
// scalar where text belongs reads as its text, such as an enum member 1 as
// "1"; any other value of the wrong kind reads as absent. An optional value
// that is empty, 0, false or null reads as absent too, and so does an
// array's element of the wrong kind, while the rest of the array is kept.
const Text = Schema.Union([Schema.String, Schema.Number, Schema.Boolean]).pipe(
  Schema.decodeTo(Schema.String, { decode: SchemaGetter.String(), encode: SchemaGetter.passthroughSubtype() }),
);
const decodeText = Schema.decodeUnknownOption(Text);
const Present = Json.check(Schema.makeFilter((j: Json) => Boolean(j)));
const orAbsent = <S extends Schema.Top>(s: S) => Schema.optionalKey(Present.pipe(Schema.decodeTo(s), Schema.catchDecoding(() => Effect.succeedNone)));
const TextList = Schema.Array(Json).pipe(Schema.decodeTo(Schema.mutable(Schema.Array(Schema.String)), {
  decode: SchemaGetter.transform((items) => items.flatMap((item) => Option.toArray(decodeText(item)))),
  encode: SchemaGetter.transform((items) => items),
}));
const decodeTextList = Schema.decodeUnknownOption(TextList);

// See Design > Answer shapes, "Shape types and validation". WarmHub's field
// format, as @warmhub/sdk-ts 0.148.0 reads it (dist/index.d.ts,
// the meta-schema above MAX_CONTENT_FIELD_BYTES; dist/chunk-IXJEQEPN.js, the
// predicates `M` and `Z` and the validator `Ze`). A spec is a type name
// ("string", or "string?" for an optional one), a typed field
// ({ type: "string?", description, ...the type's constraints }), `[T]`, or a
// nested object of specs. An object is a typed field only when its `type`
// names a WarmHub type and every other key is one that type takes with a value
// of the right kind; any other object is a nested object, whose fields include
// one named `type`. A type name WarmHub doesn't have accepts any value. A
// `pattern` is kept in the FieldDef and passed on, but the answer check doesn't
// enforce it yet: WarmHub evaluates it as RE2 with re2js, and the Tool SDK has
// neither that engine nor a safe stand-in (a JS RegExp on a curator's pattern
// is a ReDoS risk and reads some patterns differently).

// A field without a usable type gets type "unknown", which accepts any value.
const UNTYPED = "unknown";

function readText(spec: JsonObject, key: string): { [k: string]: string } {
  const v = spec[key];
  const text = v === undefined || !v ? undefined : Option.getOrUndefined(decodeText(v));
  return text === undefined ? {} : { [key]: text };
}
function readNumber(spec: JsonObject, key: string): { [k: string]: number } {
  const v = spec[key];
  return typeof v === "number" ? { [key]: v } : {};
}

/** A typed field, or undefined when `spec` isn't one: WarmHub's predicate `M`. */
function readTyped(spec: JsonObject): FieldDef | undefined {
  const written = spec["type"];
  if (typeof written !== "string") return undefined;
  const type = primitiveOf(written);
  if (type === undefined) return undefined;
  for (const [key, value] of Object.entries(spec)) {
    if (key !== "type" && key !== "description" && !constraintTest(type, key)?.(value)) return undefined;
  }
  const list = spec["enum"];
  const items = spec["items"];
  return {
    type,
    ...(written.endsWith("?") ? { optional: true } : {}),
    ...readText(spec, "description"), ...readText(spec, "shape"), ...readText(spec, "binding"), ...readText(spec, "pattern"),
    ...readNumber(spec, "minLength"), ...readNumber(spec, "maxLength"), ...readNumber(spec, "minimum"), ...readNumber(spec, "maximum"),
    ...readNumber(spec, "minItems"), ...readNumber(spec, "maxItems"),
    ...(spec["integer"] === true ? { integer: true } : {}),
    ...(Array.isArray(list) ? { enum: Option.getOrElse(decodeTextList(list), () => []) } : {}),
    ...(items !== undefined && items !== null ? { items: readItems(items) } : {}),
  };
}

function withoutOptional(field: FieldDef): FieldDef {
  const { optional: _, ...required } = field;
  return required;
}

// An array's element is never optional; WarmHub drops a "?" on it.
const readItems = (spec: Json): FieldDef => withoutOptional(readSpec(spec));

// A field written twice, as `a` and `a?`, is one WarmHub's definition check
// refuses (dist/chunk-IXJEQEPN.js, `le`, "duplicate field name"), so none
// exists; the SDK's validator would run both specs. This keeps the first and
// requires the field when either is required.
function readFields(fields: JsonObject): { [name: string]: FieldDef } {
  const read = new Map<string, FieldDef>();
  for (const [name, spec] of Object.entries(fields)) {
    const field = readSpec(spec);
    const next = name.endsWith("?") ? { ...field, optional: true } : field;
    const key = withoutQuestion(name);
    const earlier = read.get(key);
    if (earlier === undefined) read.set(key, next);
    else if (earlier.optional && !next.optional) read.set(key, withoutOptional(earlier));
  }
  return Object.fromEntries(read);
}

function readSpec(spec: Json): FieldDef {
  if (typeof spec === "string") return { type: withoutQuestion(spec), ...(spec.endsWith("?") ? { optional: true } : {}) };
  if (Array.isArray(spec)) return spec[0] === undefined ? { type: "array" } : { type: "array", items: readItems(spec[0]) };
  if (isJsonObject(spec)) return readTyped(spec) ?? { type: "object", fields: readFields(spec) };
  return { type: UNTYPED };
}

const WarmHubFields = Json.pipe(Schema.decodeTo(Schema.Record(Schema.String, FieldDef), {
  decode: SchemaGetter.transform((fields) => (isJsonObject(fields) ? readFields(fields) : {})),
  encode: SchemaGetter.forbidden(() => "A ShapeDef isn't written back as WarmHub's shape data."),
}));
const WarmHubShapeData = Schema.Struct({
  description: orAbsent(Text),
  composes: orAbsent(TextList),
  fields: Schema.optionalKey(WarmHubFields.pipe(Schema.catchDecoding(() => Effect.succeedSome({})))),
}).pipe(Schema.catchDecoding(() => Effect.succeedSome({ fields: {} })));
const decodeShapeData = Schema.decodeUnknownResult(WarmHubShapeData);

/**
 * One shape version's data, as WarmHub returns it, as a ShapeDef. A field is
 * read as WarmHub reads it: a "?" after its name or its type marks it
 * optional, a type name, `[T]` or a nested object is a field of its own, and a
 * typed field keeps its type's constraints. It never fails: data that isn't a
 * definition gives a ShapeDef with no fields, a spec that isn't a type is read
 * as type "unknown", and a value of the wrong kind is read as absent, though a
 * number or a boolean where text belongs is read as its text. An optional
 * value that is empty, 0, false or null is absent, and an enum or `composes`
 * keeps the elements that read as text.
 */
export function shapeDefFromWarmHub(repo: string, name: string, version: number, data: Json): ShapeDef {
  return { repo, version, ...manifestShapeDefOf(name, data) };
}

/**
 * A manifest shape's definition, normalized as a ShapeDef is, without a
 * repository or version: the toolset's commit, an Answer's toolVersion,
 * identifies it. See Design > Answer shapes, "Manifest shapes".
 */
export type ManifestShapeDef = Omit<ShapeDef, "repo" | "version">;

/** A manifest shape's data, in WarmHub's shape format, as a ManifestShapeDef, read as shapeDefFromWarmHub reads WarmHub's. */
export function manifestShapeDefOf(name: string, data: Json): ManifestShapeDef {
  return { name, fields: {}, ...Result.getOrElse(decodeShapeData(data), () => ({ fields: {} })) };
}
